import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Volume2, Info, ThumbsUp, ThumbsDown } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { WhWordData } from '@/data/whWordsData';

interface WhWordCardProps {
  wordData: WhWordData;
  currentLanguage: 'english' | 'french';
  speakText: (text: string, lang?: 'en-US' | 'fr-FR') => void;
}

const WhWordCard: React.FC<WhWordCardProps> = ({ wordData, currentLanguage, speakText }) => {
  const [expandedExample, setExpandedExample] = useState<number | null>(null);

  const toggleExample = (index: number) => {
    setExpandedExample(expandedExample === index ? null : index);
  };

  return (
    <Card className="overflow-hidden border-2 hover:border-primary/50 transition-all duration-300">
      <CardHeader className="bg-gradient-to-r from-primary/10 to-accent/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <HoverCard>
              <HoverCardTrigger asChild>
                <div className="cursor-help">
                  <CardTitle className="text-3xl font-bold text-primary hover:text-accent transition-colors">
                    {wordData.word}
                  </CardTitle>
                </div>
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Part of Speech</h4>
                  <p className="text-sm text-muted-foreground">{wordData.partOfSpeech}</p>
                  <h4 className="text-sm font-semibold">Pronunciation</h4>
                  <p className="text-sm text-muted-foreground font-mono">{wordData.pronunciation}</p>
                </div>
              </HoverCardContent>
            </HoverCard>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-accent border-accent">
                {wordData.french}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => speakText(wordData.word)}
                className="audio-button"
              >
                <Volume2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {currentLanguage === 'english' ? 'Hover for details' : 'Survoler pour détails'}
            </span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        <div className="space-y-6">
          {wordData.examples.map((example, index) => (
            <div key={index} className="border rounded-lg p-4 bg-secondary/30">
              {/* Split Layout: English Left, French Right */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* English Side */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="default" className="bg-primary">
                      {currentLanguage === 'english' ? 'English' : 'Anglais'}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => speakText(example.english)}
                      className="audio-button"
                    >
                      <Volume2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-lg font-medium text-primary">
                    {example.english}
                  </p>
                </div>

                {/* French Side */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="default" className="bg-accent">
                      {currentLanguage === 'english' ? 'French' : 'Français'}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => speakText(example.french, 'fr-FR')}
                      className="audio-button"
                    >
                      <Volume2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-lg font-medium text-accent">
                    {example.french}
                  </p>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Response Examples Toggle */}
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => toggleExample(index)}
                  className="flex items-center gap-2"
                >
                  {expandedExample === index ? (
                    <>
                      <ThumbsDown className="w-4 h-4" />
                      {currentLanguage === 'english' ? 'Hide Responses' : 'Masquer les Réponses'}
                    </>
                  ) : (
                    <>
                      <ThumbsUp className="w-4 h-4" />
                      {currentLanguage === 'english' ? 'Show Responses' : 'Afficher les Réponses'}
                    </>
                  )}
                </Button>
              </div>

              {/* Expanded Responses */}
              {expandedExample === index && (
                <div className="mt-4 space-y-4">
                  {/* Positive Response */}
                  <div className="grid md:grid-cols-2 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsUp className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">
                          {currentLanguage === 'english' ? 'Positive Response' : 'Réponse Positive'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => speakText(example.positiveResponse.english)}
                          className="audio-button"
                        >
                          <Volume2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-green-800">{example.positiveResponse.english}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsUp className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">
                          {currentLanguage === 'english' ? 'Positive Response' : 'Réponse Positive'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => speakText(example.positiveResponse.french, 'fr-FR')}
                          className="audio-button"
                        >
                          <Volume2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-green-800">{example.positiveResponse.french}</p>
                    </div>
                  </div>

                  {/* Negative Response */}
                  <div className="grid md:grid-cols-2 gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsDown className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-red-700">
                          {currentLanguage === 'english' ? 'Negative Response' : 'Réponse Négative'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => speakText(example.negativeResponse.english)}
                          className="audio-button"
                        >
                          <Volume2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-red-800">{example.negativeResponse.english}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsDown className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-red-700">
                          {currentLanguage === 'english' ? 'Negative Response' : 'Réponse Négative'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => speakText(example.negativeResponse.french, 'fr-FR')}
                          className="audio-button"
                        >
                          <Volume2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-red-800">{example.negativeResponse.french}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default WhWordCard;